import React, { useEffect, useState } from "react";
import { Languages, Copy as CopyIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api } from "../lib/api";

/**
 * Phase 22 — Reusable translate button + modal.
 *
 * Small "Traducir" pill button that opens a modal calling
 * `POST /api/admin/ai/translate` on demand for the given source `value`.
 * Supports AR / FR / EN with RTL rendering for Arabic.
 *
 * Props:
 *   value       — plain source text to translate (Spanish).
 *   label       — optional button label (defaults to "Traducir").
 *   size        — "sm" (default) | "xs" — tightens paddings for inline use.
 *   testid      — required per project convention; used as button testid.
 *   className   — extra classes for the button (e.g. margin overrides).
 */
export default function TranslateButton({
  value,
  label = "Traducir",
  size = "sm",
  testid,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const hasValue = !!(value && value.trim());
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={!hasValue}
        data-testid={testid || "translate-btn"}
        onClick={() => setOpen(true)}
        className={`gap-1 h-8 text-xs rounded-full ${
          size === "xs" ? "h-7 px-2" : ""
        } ${className}`}
      >
        <Languages className="h-3 w-3" />
        {label}
      </Button>
      {open && (
        <TranslateModal
          text={value}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export function TranslateModal({ text, onClose }) {
  const [target, setTarget] = useState("ar");
  const [translated, setTranslated] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!text?.trim()) return;
    setBusy(true);
    const t = toast.loading("IA traduciendo…");
    try {
      const { data } = await api.post("/admin/ai/translate", { text, target });
      setTranslated(data.text || "");
      toast.success("Traducción lista", { id: t });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail === "ai_timeout"
          ? "IA tardó demasiado"
          : "IA no disponible",
        { id: t },
      );
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(translated);
      toast.success("Copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return (
    <div
      role="dialog"
      aria-label="Traducción"
      data-testid="translate-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-lg">Traducir</h3>
          <button
            data-testid="translate-modal-close"
            onClick={onClose}
            className="h-8 w-8 grid place-items-center rounded-full hover:bg-neutral-100"
          >
            <span aria-hidden>×</span>
          </button>
        </div>
        <div className="flex gap-2">
          {[
            { v: "ar", l: "AR" },
            { v: "fr", l: "FR" },
            { v: "en", l: "EN" },
          ].map((o) => (
            <Button
              key={o.v}
              type="button"
              size="sm"
              variant={target === o.v ? "default" : "outline"}
              onClick={() => setTarget(o.v)}
              data-testid={`translate-lang-${o.v}`}
              className={target === o.v ? "bg-[#00953B] hover:bg-[#007a30]" : ""}
            >
              {o.l}
            </Button>
          ))}
        </div>
        <div className="text-xs text-neutral-500">Original</div>
        <pre className="text-sm bg-neutral-50 p-2 rounded whitespace-pre-wrap max-h-40 overflow-auto">
          {text}
        </pre>
        <div className="text-xs text-neutral-500">
          Traducción {busy ? "(generando…)" : ""}
        </div>
        <pre
          data-testid="translate-modal-result"
          dir={target === "ar" ? "rtl" : "ltr"}
          className="text-sm bg-emerald-50 p-2 rounded whitespace-pre-wrap min-h-[3rem] max-h-56 overflow-auto"
        >
          {translated || (busy ? "" : "—")}
        </pre>
        <div className="flex justify-end gap-2 pt-2 border-t border-neutral-100">
          <Button
            size="sm"
            variant="outline"
            onClick={copy}
            disabled={!translated}
            data-testid="translate-modal-copy"
          >
            <CopyIcon className="h-3 w-3 mr-1" /> Copiar
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}
